import React from "react";
import {
  ClientsByEnabledApplicationReport,
  EnabledApplicationsReportItem,
  MetricsService,
  ApplicationCollection,
  ApplicationsService
} from "gen/clients";
import { ExportButton } from "components/ExportButton/ExportButton";
import { default as dayjs } from "dayjs";
import { useQuery } from "react-query";

interface Props {
  applicationId?: string;
  applicationName?: string;
}

interface ExportedDataRow {
  clientName: string;
  [key: string]: string | number | boolean;
}

const ExportClientsByEnabledApplicationButton: React.FC<Props> = ({ applicationId, applicationName }) => {
  const { data: apps } = useQuery<ApplicationCollection>(["applications"], ApplicationsService.listApplications, {
    refetchOnMount: false,
    refetchOnWindowFocus: false
  });

  const getExportData = React.useCallback(async (): Promise<ExportedDataRow[]> => {
    if (!applicationId) {
      return [];
    }

    let allItems: EnabledApplicationsReportItem[] = [];
    let nextPageToken: string | undefined = undefined;

    // Keep fetching until no more pages
    do {
      const response: ClientsByEnabledApplicationReport = await MetricsService.getClientsByEnabledApplicationReport({
        applicationId,
        pageToken: nextPageToken,
        pageSize: 100 // Fetch 100 items at a time
      });

      allItems = [...allItems, ...response.items];
      nextPageToken = response.nextPageToken;
    } while (nextPageToken);

    // Sort applications by name
    const sortedApps =
      apps?.items.sort((app1, app2) => (app1?.name || app1.id).localeCompare(app2?.name || app2.id)) || [];

    // Map the data to match the CSV columns
    return allItems.map(row => {
      const exportRow: ExportedDataRow = {
        clientName: row.clientName
      };

      // Add a column for each application
      sortedApps.forEach(app => {
        const appName = app.name || app.id;
        exportRow[appName] = row.enabledApplications.includes(app.id) ? "Yes" : "No";
      });

      return exportRow;
    });
  }, [applicationId, apps]);

  // Generate column headers dynamically based on applications
  const columnHeaders = React.useMemo(() => {
    const headers: Record<string, string> = {
      clientName: "Client Name"
    };

    const sortedApps =
      apps?.items.sort((app1, app2) => (app1?.name || app1.id).localeCompare(app2?.name || app2.id)) || [];

    sortedApps.forEach(app => {
      const appName = app.name || app.id;
      headers[appName] = appName;
    });

    return headers;
  }, [apps]);

  const fileName = `clients-with-${applicationName || applicationId || "application"}-${dayjs().format("MM-DD-YYYY")}.csv`;

  return (
    <ExportButton<ExportedDataRow>
      filename={fileName}
      columnHeaders={columnHeaders}
      getExportData={getExportData}
      exportingMessage="Exporting clients report. This may take a moment..."
      variant="outlined"
      disabled={!applicationId || !apps}
    >
      Export to CSV
    </ExportButton>
  );
};

export { ExportClientsByEnabledApplicationButton };
